<template>
  <v-sheet color="white" elevation="5" rounded class="mt-5 mb-5 pt-3 pb-5 pl-3 pr-3">
    <v-spacer />
    <v-spacer />
    <v-row class="pa-3">
      <v-col>
        <v-row>
          <v-col>
            <datepicker
              :date="date"
              label="Date*"
              v-on:updateDates="updateDate($event)"
            />
          </v-col>
          <v-col>
            <v-select :items="showTypes" :item-text="toItemText" label="Show Type" item-value="showTypeId" v-model="showTypeId"
              @input="$emit('update', $event)" />
          </v-col>
          <v-spacer/>
          <v-col>
            <v-text-field v-model="search" append-icon="mdi-magnify" label="Search" single-line hide-details />
          </v-col>  
          <v-col cols="1">            
            <v-btn outlined color="primary" :disabled="results?.length == 0 || !date"
                  v-on:click="csvExport()">
                <font-awesome-icon icon="file-download" size="lg" />
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table :headers="showResultsHeaders" :items="results" dense
          :options.sync="options" :server-items-length="totalCount" disable-sort
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }" fixed-header >
          <template v-slot:item.showDate="{ item }">
            <localized-date :date="item.showDate" />
          </template>          
          <template v-slot:item.isScchMajor="{ item }">
            <span v-if="item.isScchMajor != null">
              {{ item.isScchMajor ? 'Yes' : 'No' }}
            </span>
            <span v-else></span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import Datepicker from "@/components/common/inputs/DatepickerFormatted.vue";

import showResultsService from "@/services/showResultsService";
import showTypesService from "@/services/showTypesService";
import { ShowType } from "@/types/admin/Show";
import { getUserToday } from "@/helpers/date-utils";
import { PaginationOptions } from "@/types/Pagination";
import { debounce } from "throttle-debounce";
import { downloadCsv } from "@/helpers/download-csv";

const noneOption = [{ showTypeId: "", description: 'All' }] as ShowType[];

export default Vue.extend({
  name: "ShowResultsPage",
  components: { Datepicker },
  data: function () {
    return {
      showResultsHeaders: [
        { text: "Show Date", align: "left", value: "showDate" },
        { text: "Dog Id", align: "left", value: "dogId" },
        { text: "Dog Name", align: "left", value: "dogName" },
        { text: "Descr.", align: "left", value: "description" },
        { text: "City, State", align: "left", value: "cityState" },
        { text: "Country", align: "left", value: "country" },
        { text: "Category", align: "left", value: "category" },
        { text: "Time", align: "left", value: "time" },
        { text: "Division", align: "left", value: "division" },
        { text: "Score", align: "left", value: "score" },
        { text: "SCCH Score", align: "left", value: "scchScore" },
        { text: "SCCH Major", align: "left", value: "isScchMajor" },
        { text: "Place", align: "left", value: "place" },
        { text: "Hit", align: "left", value: "hit" },
        { text: "Pt Sched.", align: "left", value: "cpSched" },
        { text: "# Competing", align: "left", value: "nCompeting" },
        { text: "Handler", align: "left", value: "handler" },
        { text: "Judge", align: "left", value: "judge" },
      ],
      results: [],
      search: "",
      showTypeId: "",
      date: getUserToday(),
      showTypes: [] as ShowType[],
      loadRequestId: 0,
      loadShowResultsForSearch: null as any,
      totalCount: 0,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: false,
      } as PaginationOptions,
    };
  },
  methods: {
    toItemText: function (item: ShowType) {
      return item.showTypeId ? `${item.showTypeId}: ${item.description}` : item.description
    },
    updateDate: function (val: any) {
      this.date = val?.date;
    },
    csvExport: function () {
      if (!this.date) return;

      const filename = `ShowResults_${this.showTypeId == "" ? 'ALL' : this.showTypeId}_${this.date.toString()}.csv`;
      downloadCsv(filename, this.results);
    },
    loadShowResults: function () {
      const requestId = ++this.loadRequestId;
      if (!this.date) {
        this.results = [];
        this.totalCount = 0;
        this.$store.dispatch("changeLoaderStatus", false);
        return;
      }

      this.$store.dispatch("changeLoaderStatus", true);
      showResultsService
        .getShowResults({
          date: this.date,
          showTypeId: this.showTypeId,
          search: this.search,
          page: this.options.page - 1,
          pageSize: this.options.itemsPerPage,
        })
        .then((res) => {
          if (requestId === this.loadRequestId) {
            this.results = res.data;
            this.totalCount = res.totalCount;
          }
        })
        .finally(() => {
          if (requestId === this.loadRequestId) this.$store.dispatch("changeLoaderStatus", false);
        });
    },
  },
  created: function () {
    this.loadShowResultsForSearch = debounce(300, () => {
      if (this.options.page === 1) this.loadShowResults();
      else this.options.page = 1;
    });
    showTypesService.getShowTypes()
      .then((data) => {
        this.showTypes = noneOption.concat(data)
      })
      .catch(this.$handleError);
  },
  beforeDestroy() {
    this.loadShowResultsForSearch.cancel();
  },
  watch: {
    date() {
      this.loadShowResultsForSearch.cancel({ upcomingOnly: true });
      if (this.options.page === 1) this.loadShowResults();
      else this.options.page = 1;
    },
    showTypeId() {
      this.loadShowResultsForSearch.cancel({ upcomingOnly: true });
      if (this.options.page === 1) this.loadShowResults();
      else this.options.page = 1;
    },
    search() {
      this.loadRequestId++;
      this.$store.dispatch("changeLoaderStatus", false);
      this.loadShowResultsForSearch();
    },
    options: {
      handler() {
        this.loadShowResults();
      },
      deep: true,
    },
  },
});
</script>
